import httpService from "../helper/httpService";
import {menuItems} from "@/store/config/menu";

export const RIGHTS = {
  MAKLERINKASSO: "Maklerinkasso",
  ANGEBOTE: "Angebote",
  ANTRAEGE: "Anträge",
  BESTAND: "Bestand",
  COURTAGEN: "Courtagen",
  EXCEL: "Excel",
  PROFIL: "Profil",
  OEKO: "Öko",
  MAKLERNUMMER: "Maklernummer",
  TARIFSELECTION: "TarifSelection",
  GEWERBEABSCHLUSS: "GewerbeAbschluss",
  VERTRIEBSLEITER: "Vertriebsleiter",
  KEINABSCHLUSSBUTTON: "Abschluss"
};

const pageToRightMapping = {
  BenutzerProfil: RIGHTS.PROFIL,
  Courtagen: RIGHTS.COURTAGEN,
  Angebote: RIGHTS.ANGEBOTE,
  OffeneAntraegePage: RIGHTS.ANTRAEGE,
  AntraegeSearchPage: RIGHTS.TARIFSELECTION,
  MaklerActivationPage: RIGHTS.TARIFSELECTION,
  Bestand: RIGHTS.BESTAND,
  BestandDetailPage: RIGHTS.BESTAND,
  VLBestandDetailPage: RIGHTS.BESTAND,
  Schaden: RIGHTS.BESTAND,
  SchadenDetailPage: RIGHTS.BESTAND,
  Aerzte: RIGHTS.GEWERBEABSCHLUSS,
  Gewerbekunden: RIGHTS.GEWERBEABSCHLUSS,
  VLSchadenDetailPage: RIGHTS.BESTAND
};

const rightsDefinitions = {
  [RIGHTS.MAKLERINKASSO]: { propertyName: "MAKLERINKASSO", valueToCheck: "1", inverse: false },
  [RIGHTS.ANGEBOTE]: { propertyName: "KEINEANGEBOTSLISTE", valueToCheck: "1", inverse: true },
  [RIGHTS.ANTRAEGE]: { propertyName: "WEBIDSCHWEB", valueToCheck: "[]", inverse: true },
  [RIGHTS.BESTAND]: { propertyName: "VMNRVERTRAG", valueToCheck: "[]", inverse: true },
  [RIGHTS.COURTAGEN]: { propertyName: "VMNRPROVISION", valueToCheck: "[]", inverse: true },
  [RIGHTS.EXCEL]: { propertyName: "KEINEXCEL", valueToCheck: "1", inverse: true },
  [RIGHTS.PROFIL]: { propertyName: "HIDEBENUTZERDATEN", valueToCheck: "1", inverse: true },
  [RIGHTS.OEKO]: { propertyName: "OEKOPUNKTE", valueToCheck: "1", inverse: false },
  [RIGHTS.MAKLERNUMMER]: { propertyName: "MAKNUMMER", valueToCheck: "1", inverse: false },
  [RIGHTS.TARIFSELECTION]: { propertyName: "TARIFSELECTION", valueToCheck: "1", inverse: false },
  [RIGHTS.GEWERBEABSCHLUSS]: { propertyName: "KEINGW", valueToCheck: "1", inverse: true },
  [RIGHTS.VERTRIEBSLEITER]: { propertyName: "VERTRIEBSLEITER", valueToCheck: null, inverse: false },
  [RIGHTS.KEINABSCHLUSSBUTTON]: { propertyName: "KEINABSCHLUSSBUTTON", valueToCheck: "1", inverse: false }
};

const getters = {
  isAuthenticated: (state) => {
    // if cookie exists, name is set before App is started
    return state.username !== null;
  },
  user: (state) => {
    return {
      name: state.username,
      vmnr: state.vmnr
    };
  },
  nameForMenu: (state) => {
    return state.metadata.firstname && state.metadata.lastname ? 
      state.metadata.firstname + " " + state.metadata.lastname :
      state.metadata.firstname ? 
        state.metadata.firstname :
        state.metadata.lastname ? 
          state.metadata.lastname :
          state.metadata.company;
  },
  isIatUser: (state) => {
    return state.maklertyp === "20";
  },
  firstLogin: (state) => {
    return state.logincount === 1;
  },
  rabattVollmacht: (state, getters) => {
    if (state.rabattvollmacht === "1") return true;
    return getters.checkAuthorization("vollmacht");
  },
  checkAuthorization: (state) => {
    return function (right) {
      const rightsDefinition = rightsDefinitions[right];
      if (!rightsDefinition) {
        return false;
      }
      const isRightSet = state.authorization.rights.some(userRight =>
        userRight.name === rightsDefinition.propertyName &&
        (!rightsDefinition.valueToCheck || userRight.value === rightsDefinition.valueToCheck));

      return rightsDefinition.inverse ? !isRightSet : isRightSet;
    };
  },
  checkAuthorizationPKS: (state, getters) => {
    return getters.checkAuthorization(RIGHTS.TARIFSELECTION);},
  checkPageAuthorization: (state, getters) => {
    return function (pageName) {
      return getters.checkAuthorization(pageToRightMapping[pageName]);
    };
  },
  showSuperuserAngebote: (state) => {
    return state.authorization.rights.some(userRight => userRight.name === "ANGEBOTSLISTEDMBUSERS" && !!userRight.value);
  },
  menuItems: (state, getters) => {
    let userText = state.username;
    if (state.vmnr) {
      userText = userText + ` (${state.vmnr})`;
    }
    return menuItems({
      pks: getters.checkAuthorization(RIGHTS.TARIFSELECTION),
      angebote: getters.checkAuthorization(RIGHTS.ANGEBOTE),
      antraege: getters.checkAuthorization(RIGHTS.ANTRAEGE),
      bestand: getters.checkAuthorization(RIGHTS.BESTAND),
      courtagen: getters.checkAuthorization(RIGHTS.COURTAGEN),
      profil: getters.checkAuthorization(RIGHTS.PROFIL),
      aerzterechner: getters.checkAuthorization(RIGHTS.GEWERBEABSCHLUSS),
      gewerberechner: getters.checkAuthorization(RIGHTS.GEWERBEABSCHLUSS)
    }, userText);
  }
};

const actions = {
  checkAuth(context) {
    return httpService.getWhoAmI()
      .then(user => {
        context.commit("setUser", user);
      })
      .catch(err => {
        console.log(`AUTH check failed with ${err.status}: ${err.message}`);
        context.commit("clearUser");
      });
  },
  logout(context) {
    context.commit("resetSteps", { page: "aenderung" });
    context.commit("resetSteps", { page: "privat" });
    context.commit("resetSteps", { page: "gewerbe" });
    context.commit("resetAngebotData");
    context.commit("gewerbeResetAngebotData");
    context.commit("clearUser");
  }
};

const mutations = {
  setUser(state, newUser) {
    Object.assign(state, initialState(), newUser);
  },
  clearUser(state) {
    Object.assign(state, initialState());
  }
};

function initialState() {
  return {
    metadata: {},
    username: null,
    vmnr: null,
    tarifsatz: null,
    maklertyp: null,
    logincount: 0,
    rabattvollmacht: "0",
    tarifname: null,
    authorization: {
      roles: [],
      rights: []
    }
  };
}

export default {
  state: initialState(),
  getters,
  actions,
  mutations
};
